import { Box } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import LazyLoad from "goi_common/src/components/LazyLoad";
import { ReactNode, useMemo } from "react";

interface HomeV4YoutubeVerticalSectionProps {
  title?: ReactNode;
  description?: ReactNode;
  youtubeUrl?: string;
}

export default function HomeV4YoutubeVerticalSection({
  title = (
    <>
      100원 상조와
      <br />
      뭐가 다른가요?
    </>
  ),
  description = (
    <>
      고이 대표님이 직접 알려드려요.
      <br />
      보장 금액은 <b>279만원(2025 기준)으로 동일</b>해요.
      <br />
      고객님 상황에 맞게 납입 방식만 선택하세요.
    </>
  ),
  youtubeUrl = "",
}: HomeV4YoutubeVerticalSectionProps) {
  // YouTube Shorts URL을 embed URL로 변환
  const embedUrl = useMemo(() => {
    if (!youtubeUrl) return "";

    // Shorts URL 패턴: youtube.com/shorts/VIDEO_ID
    const shortsMatch = youtubeUrl.match(
      /(?:youtube\.com\/shorts\/|youtu\.be\/shorts\/)([a-zA-Z0-9_-]+)/
    );
    if (shortsMatch) {
      return `https://www.youtube.com/embed/${shortsMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${shortsMatch[1]}&rel=0&modestbranding=1`;
    }

    // 일반 embed URL이면 그대로 사용
    if (youtubeUrl.includes("youtube.com/embed/")) {
      return youtubeUrl;
    }

    // 일반 YouTube URL 패턴: youtube.com/watch?v=VIDEO_ID
    const watchMatch = youtubeUrl.match(
      /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]+)/
    );
    if (watchMatch) {
      return `https://www.youtube.com/embed/${watchMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${watchMatch[1]}&rel=0&modestbranding=1`;
    }

    return youtubeUrl;
  }, [youtubeUrl]);

  return (
    <BaseStack width="100%" padding="45px 20px" alignItems="center">
      <Box className="gray900 head_24_sb" textAlign="center">
        {title}
      </Box>

      <Spacing gap={16} />

      <Box className="gray700 body_16_r" textAlign="center">
        {description}
      </Box>

      <Spacing gap={24} />

      {/* 유튜브 세로형 영상 */}
      <VerticalVideoContainer>
        <LazyLoad width="100%" height="100%" top={-400}>
          <VerticalIframeWrapper>
            <YoutubeIframe
              src={embedUrl}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </VerticalIframeWrapper>
        </LazyLoad>
      </VerticalVideoContainer>
    </BaseStack>
  );
}

// 세로형 영상 컨테이너 (최대 너비 제한)
const VerticalVideoContainer = styled.div`
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
`;

// 세로형 영상 비율 (9:16)
const VerticalIframeWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 177.78%; /* 16/9 * 100 = 177.78% (9:16 비율) */
  border-radius: 8px;
  overflow: hidden;
`;

const YoutubeIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;
